import React, { useState, useRef, useEffect, useMemo } from "react";
import TodayTimeSlot from "./TodayTimeSlot";
import { GripHorizontal, Clock, PawPrint, Phone, UserCheck, MessageSquare } from "lucide-react";
import { useUpdateDurationMutation } from "../../../redux/actions/Admin/ServiceAdmin";

export interface TodayTimelineProps {
  services: any[];
  workingStartTime?: string;
  workingEndTime?: string;
  handlers: {
    onServiceClick: (service: any) => void;
    onEmptySlotClick?: (time: string, dateStr?: string) => void;
    onAssignEmployee?: (booking: any) => void;
    onReschedule?: (booking: any) => void;
    onUpdateDuration?: (booking: any) => void;
  };
}

// Parses "09:00:00" -> 9
const parseWorkingHour = (timeStr?: string, defaultHour: number = 9): number => {
  if (!timeStr) return defaultHour;
  const hour = parseInt(timeStr.split(":")[0], 10);
  return isNaN(hour) ? defaultHour : hour;
};

// Converts "HH:mm:ss" or "HH:mm" to "HH:mm" (strips seconds)
const formatTimeHHMM = (timeStr?: string): string => {
  if (!timeStr) return "";
  const parts = timeStr.split(":");
  if (parts.length >= 2) {
    return `${parts[0]}:${parts[1]}`;
  }
  return timeStr;
};

// Formats duration minutes into "1.30hr", "1.45hr", "0.45hr" format
const formatDurationToHr = (totalMinutes?: number): string => {
  if (!totalMinutes || totalMinutes <= 0) return "0.00hr";
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const formattedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;
  
  if (hours === 0) {
    return `0.${formattedMinutes}hr`;
  }
  return `${hours}.${formattedMinutes}hr`;
};

// Extract Add-On Names from serviceDataJson
const getAddOnNames = (serviceDataJson?: string): string => {
  if (!serviceDataJson) return "";
  try {
    const parsed = JSON.parse(serviceDataJson);
    if (parsed && Array.isArray(parsed.selectedAddOns) && parsed.selectedAddOns.length > 0) {
      return parsed.selectedAddOns.map((item: any) => item.name).filter(Boolean).join(", ");
    }
  } catch (e) {
    console.error("Failed to parse serviceDataJson:", e);
  }
  return "";
};

// Converts time string "HH:mm:ss" or "HH:mm" to decimal hours (e.g. "09:50:00" -> 9.833)
// Converts time string "HH:mm:ss" or "HH:mm" to decimal hours safely
const timeToDecimalHours = (timeStr?: string): number => {
  if (!timeStr) return 0;
  const parts = timeStr.split(":");
  if (!parts || parts.length === 0) return 0;

  let h = Number(parts[0]);
  const m = parts[1] ? Number(parts[1]) : 0;
  
  const upperStr = timeStr.toUpperCase();
  const isPM = upperStr.includes("PM");
  const isAM = upperStr.includes("AM");

  // If explicit 12-hour AM/PM format is used:
  if (isPM && h !== 12) h += 12;
  if (isAM && h === 12) h = 0;

  // If it's a 24-hour format string (e.g. "12:00:00" or "13:00:00"), 
  // 12 remains 12 (noon), and 13 remains 13 (1 PM), etc.

  return h + m / 60;
};

// Converts "HH:mm:ss" + duration in minutes -> new "HH:mm:ss"
const addMinutesToTime = (startTimeStr: string, addedMinutes: number): string => {
  const [h, m] = startTimeStr.split(":").map(Number);
  const date = new Date();
  date.setHours(h);
  date.setMinutes(m + addedMinutes);

  const newH = String(date.getHours()).padStart(2, "0");
  const newM = String(date.getMinutes()).padStart(2, "0");
  return `${newH}:${newM}:00`;
};

const SLOT_HEIGHT = 88; // Height in pixels per 1 hour slot

const TodayTimeline: React.FC<TodayTimelineProps> = ({
  services = [],
  workingStartTime = "09:00:00",
  workingEndTime = "18:00:00",
  handlers,
}) => {
  // Dynamic Start & End Hours calculated from Service Data
  const startHour = parseWorkingHour(workingStartTime, 9);
  const endHour = parseWorkingHour(workingEndTime, 18);
  const totalSlots = Math.max(1, endHour - startHour + 1);

  // Dynamic Hours array for slots rendering
  const hours = Array.from({ length: totalSlots }, (_, i) => i + startHour);

  const [updateDuration] = useUpdateDurationMutation();

  // Drag Resizing State
  const [resizingBookingId, setResizingBookingId] = useState<number | null>(null);
  const [tempEndTime, setTempEndTime] = useState<string | null>(null);
  const startYRef = useRef<number>(0);
  const initialDurationRef = useRef<number>(45);

  // ----------------------------------------------------
  // OVERLAP ALGORITHM: Group & layout concurrent bookings
  // ----------------------------------------------------
  const processedBookings = useMemo(() => {
    const items = services.map((service, index) => {
      const bId = service.bookingId || service.id || index;
      const isResizingThis = resizingBookingId === bId;

      const startDec = timeToDecimalHours(service.startTime);
      const displayEnd = isResizingThis && tempEndTime ? tempEndTime : service.endTime;
      const endDec = timeToDecimalHours(displayEnd);

      return {
        ...service,
        bId,
        startDec,
        endDec,
        durationHours: Math.max(0.25, endDec - startDec),
        isResizingThis,
        displayEnd,
      };
    });

    items.sort((a, b) => a.startDec - b.startDec || b.durationHours - a.durationHours);

    const layoutItems: any[] = [];
    let currentGroup: any[] = [];
    let groupEnd = 0;

    items.forEach((item) => {
      if (item.startDec < groupEnd) {
        currentGroup.push(item);
        groupEnd = Math.max(groupEnd, item.endDec);
      } else {
        if (currentGroup.length > 0) {
          assignColumns(currentGroup, layoutItems);
        }
        currentGroup = [item];
        groupEnd = item.endDec;
      }
    });

    if (currentGroup.length > 0) {
      assignColumns(currentGroup, layoutItems);
    }

    return layoutItems;
  }, [services, resizingBookingId, tempEndTime]);

  function assignColumns(group: any[], layoutItems: any[]) {
    const totalCols = group.length;
    group.forEach((item, colIndex) => {
      layoutItems.push({
        ...item,
        colIndex,
        totalCols,
        widthPct: 100 / totalCols,
        leftPct: (colIndex * 100) / totalCols,
      });
    });
  }

  // Handle Drag Move & Drop
  useEffect(() => {
    if (resizingBookingId === null) return;

    const activeService = services.find(
      (s) => (s.bookingId || s.id) === resizingBookingId
    );
    if (!activeService) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaY = e.clientY - startYRef.current;
      const step15Mins = Math.round(deltaY / 20) * 15;
      const newDuration = Math.max(15, initialDurationRef.current + step15Mins);

      const calculatedEndTime = addMinutesToTime(
        activeService.startTime || "10:00:00",
        newDuration
      );
      setTempEndTime(calculatedEndTime);
    };

    const handleMouseUp = async () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);

      if (tempEndTime && tempEndTime !== activeService.endTime) {
        try {
          await updateDuration({
            bookingId: activeService.bookingId,
            newEndTime: tempEndTime,
            remarks: "Updated via drag timeline handle",
          }).unwrap();
        } catch (err) {
          alert("Failed to update booking duration via drag.");
        }
      }

      setResizingBookingId(null);
      setTempEndTime(null);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [resizingBookingId, tempEndTime, services, updateDuration]);

  // Status Style Theme Helper
  const getStatusTheme = (status?: string) => {
    switch (status?.toLowerCase()) {
      case "confirmed":
      case "completed":
        return {
          bg: "bg-emerald-50/95 border-emerald-300 hover:border-emerald-400",
          accent: "bg-emerald-500",
          badge: "bg-emerald-100 text-emerald-800 border-emerald-200",
          text: "text-emerald-950",
        };
      case "in-progress":
      case "inprogress":
        return {
          bg: "bg-blue-50/95 border-blue-300 hover:border-blue-400",
          accent: "bg-blue-500",
          badge: "bg-blue-100 text-blue-800 border-blue-200",
          text: "text-blue-950",
        };
      case "cancelled":
        return {
          bg: "bg-rose-50/95 border-rose-300 hover:border-rose-400",
          accent: "bg-rose-500",
          badge: "bg-rose-100 text-rose-800 border-rose-200",
          text: "text-rose-950",
        };
      default: // Pending / Rescheduled
        return {
          bg: "bg-amber-50/95 border-amber-300 hover:border-amber-400",
          accent: "bg-amber-500",
          badge: "bg-amber-100 text-amber-800 border-amber-200",
          text: "text-amber-950",
        };
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden relative select-none shadow-xs">
      {/* ---------------------------------------------------- */}
      {/* 1. BACKGROUND TIME SLOTS GRID                        */}
      {/* ---------------------------------------------------- */}
      {hours.map((hour) => (
        <TodayTimeSlot
          key={hour}
          hour={hour}
          services={[]}
          handlers={handlers}
        />
      ))}

      {/* ---------------------------------------------------- */}
      {/* 2. OVERLAY FOR DYNAMIC SIDE-BY-SIDE SPANNING CARDS   */}
      {/* ---------------------------------------------------- */}
      <div className="absolute top-0 bottom-0 left-28 right-0 pointer-events-none pr-3">
        {processedBookings.map((service) => {
          if (!service.startDec || !service.endDec) return null;

          // Position calculated relative to dynamic startHour
          const topPx = (service.startDec - startHour) * SLOT_HEIGHT;
          const heightPx = service.durationHours * SLOT_HEIGHT - 6;
          const totalMinutes = Math.round(service.durationHours * 60);
          const isCompact = heightPx < 70; // Adaptive compact mode

          const theme = getStatusTheme(service.status);
          const addOns = getAddOnNames(service.serviceDataJson);

          const startTimeFormatted = formatTimeHHMM(service.startTime);
          const endTimeFormatted = formatTimeHHMM(service.displayEnd);
          const durationFormatted = formatDurationToHr(totalMinutes);

          return (
            <div
              key={service.bId}
              style={{
                top: `${topPx + 3}px`,
                height: `${heightPx}px`,
                width: `calc(${service.widthPct}% - 8px)`,
                left: `calc(${service.leftPct}% + 4px)`,
              }}
              onClick={(e) => {
                e.stopPropagation();
                handlers.onServiceClick(service);
              }}
              className={`absolute pointer-events-auto border ${theme.bg} ${
                service.isResizingThis
                  ? "shadow-2xl z-30 ring-2 ring-amber-400 scale-[1.01]"
                  : "shadow-xs z-10 hover:shadow-md"
              } rounded-xl overflow-hidden flex flex-col justify-between transition-all cursor-pointer group`}
            >
              {/* Accent Color Stripe */}
              <div className={`absolute top-0 bottom-0 left-0 w-1.5 ${theme.accent}`} />

              {/* CARD CONTENT */}
              <div className="p-2.5 pl-3.5 flex flex-col h-full justify-between overflow-hidden">
                {/* COMPACT MODE FOR SMALL TIMINGS (< 60 MINS) */}
                {isCompact ? (
                  <div className="flex items-center justify-between gap-1 my-auto">
                    <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                      <span className={`font-bold ${theme.text} text-xs truncate`}>
                        {service.customerName}
                      </span>
                      <span className="text-[10px] font-bold text-slate-700 truncate">
                        ({service.petName})
                      </span>
                      {addOns && (
                        <span className="bg-amber-200/90 text-amber-900 text-[9px] font-extrabold px-1 rounded border border-amber-300">
                          + {addOns}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      <span className="text-[10px] bg-white/80 border border-black/10 px-1.5 py-0.2 rounded font-semibold text-slate-700">
                        {startTimeFormatted} - {endTimeFormatted}
                      </span>
                      <span
                        className={`text-[9px] px-1.5 py-0.2 rounded-full font-bold border ${theme.badge}`}
                      >
                        {service.status}
                      </span>
                    </div>
                  </div>
                ) : (
                  /* FULL DETAILED MODE FOR STANDARD/TALL TIMINGS (>= 60 MINS) */
                  <>
                    <div className="space-y-1">
                      {/* Top Bar: Customer Name & Status Badge */}
                      <div className="flex justify-between items-start gap-1">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className={`font-bold ${theme.text} text-xs sm:text-sm truncate`}>
                              {service.customerName}
                            </span>
                            {service.phoneNumber && (
                              <span className="text-[10px] text-slate-500 font-medium flex items-center gap-0.5">
                                <Phone className="w-2.5 h-2.5 text-slate-400" />
                                {service.phoneNumber}
                              </span>
                            )}
                          </div>
                        </div>

                        <span
                          className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${theme.badge} shrink-0`}
                        >
                          {service.status}
                        </span>
                      </div>

                      {/* Subtitle Bar: Pet Name + Add-ons + Package */}
                      <div className="text-slate-600 font-medium text-[11px] flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                        <div className="flex items-center gap-1">
                          <PawPrint className="w-3 h-3 text-slate-400 shrink-0" />
                          <span className="font-bold text-slate-800">{service.petName}</span>
                        </div>

                        {/* Add-ons next to pet name */}
                        {addOns && (
                          <span className="bg-amber-200/80 text-amber-900 text-[10px] font-extrabold px-1.5 py-0.5 rounded border border-amber-300">
                            + {addOns}
                          </span>
                        )}

                        <span className="text-slate-300">•</span>
                        <span className="truncate text-slate-600 font-semibold">{service.packageName}</span>
                      </div>

                      {/* Staff Assignment */}
                      {service.employeeName && (
                        <div className="text-[10px] text-slate-600 flex items-center gap-1 font-semibold">
                          <UserCheck className="w-3 h-3 text-emerald-600 shrink-0" />
                          <span>Staff: {service.employeeName}</span>
                        </div>
                      )}

                      {/* Remarks / Notes */}
                      {service.remarks && (
                        <div className="text-[10px] text-amber-800 bg-amber-100/60 p-1 rounded border border-amber-200/80 flex items-center gap-1 italic truncate">
                          <MessageSquare className="w-2.5 h-2.5 text-amber-600 shrink-0" />
                          <span className="truncate">{service.remarks}</span>
                        </div>
                      )}
                    </div>

                    {/* Bottom Bar: Start/End Time (No Seconds) & Duration in hr Format */}
                    <div className="flex flex-wrap items-center justify-between gap-1 mt-auto pt-1.5 border-t border-black/5 text-[11px] font-semibold text-slate-700">
                      <div className="flex items-center gap-1 text-slate-800 font-bold">
                        <Clock className="w-3 h-3 text-slate-400 shrink-0" />
                        <span>
                          {startTimeFormatted} - {endTimeFormatted}
                        </span>
                      </div>

                      <span className="bg-white/90 border border-black/10 px-2 py-0.5 rounded-md text-[10px] text-slate-800 shadow-2xs font-extrabold">
                        {durationFormatted}
                      </span>
                    </div>
                  </>
                )}
              </div>

              {/* Bottom Drag Handle for Resizing Duration */}
              <div
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setResizingBookingId(service.bId);
                  startYRef.current = e.clientY;
                  initialDurationRef.current =
                    service.durationMinutes || Math.round(service.durationHours * 60);
                }}
                className="w-full h-3.5 bg-black/5 hover:bg-amber-400/80 rounded-b-xl cursor-ns-resize flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 mt-auto shrink-0"
                title="Drag down or up to change duration"
              >
                <GripHorizontal className="w-4 h-3 text-slate-700" />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TodayTimeline;